import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppSearch, AppSearchData, AppSearchState, AppSearchFilter } from "./AppSearch";
import { AppSearchIndex } from "./AppSearchIndex";
import { AppSource } from "./AppSource";
import { AppSearchRemote } from "./AppSearchRemote";
import { AppMessageContext } from "./AppMessageContext";
import { AppSearchActView } from "./AppSearchActions";
import { AppSearchActRun, IAppSearchActHost, ISaEl } from "./AppSearchActRun";

// AppSearchPanel - the "Messages" half of the main screen's search: loads `search.json` when it is
// first needed and paints the hits into `wac-msghits`. The rules are `AppSearch`'s; this is the act.
//
// ⛔ ONLY `wac-msghits` IS EVER WRITTEN - never the box. Repainting the input would take focus and
// the caret away mid-word, and on a phone close the keyboard after every letter.
//
// ⚠ LAZY: nothing is fetched until a query of two folded characters arrives, and then ONCE per page
// (`AppSearchState`). A subscriber who never searches never pays for the file.
//
// ⚠ ONE PANEL PER SOURCE. A new archive (a sign-in, a new ticket) gets a new panel and the old one
// is `Stop`ped, so a late answer for the old archive cannot paint over the new one.

export class AppSearchPanel {
    /** Name filtering is immediate; the message search waits for a pause in typing. */
    public static readonly DebounceMs: number = 250;

    private Source: AppSource;
    private State: AppSearchState = new AppSearchState();
    private Query: string = "";
    private Timer: number = 0;
    private Stopped: boolean = false;
    private Filter: AppSearchFilter = new AppSearchFilter();
    private Remote: AppSearchRemote;
    private Context: AppMessageContext;
    /** A paging button was pressed and its page has not been painted yet - the next page's first result takes the focus. */
    private Paging: boolean = false;

    /** ⭐ (2026-10-22) The actions on each hit; null = the plain hits (no page host). */
    private Acts: AppSearchActRun | null = null;
    private ActHost: IAppSearchActHost | null = null;

    public constructor(Source: AppSource, ActHost: IAppSearchActHost | null = null) {
        this.Source = Source;
        this.ActHost = ActHost;
        if (ActHost !== null) { this.Acts = new AppSearchActRun(ActHost); }
        this.Remote = new AppSearchRemote(Source, this.Acts);
        this.Context = new AppMessageContext(Source);
    }

    /** The box changed. ⚠ Debounced - a phone typist makes a request-sized pause every word. */
    public Typed = (Filter: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Query = nz(Filter);
            this.Filter.Offset = 0;
            this.Paging = false;
            this.Remote.Reset();
            if (this.Timer !== 0) { window.clearTimeout(this.Timer); this.Timer = 0; }
            if (AppSearch.Ready(this.Query) === false) { this.Render(""); return; }
            this.Timer = window.setTimeout(this.OnDue, AppSearchPanel.DebounceMs);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.Typed");
        }
    };

    /** The list was repainted with a filter in it: search again at once, no debounce. */
    public Now = (Filter: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Query = nz(Filter);
            if (this.Timer !== 0) { window.clearTimeout(this.Timer); this.Timer = 0; }
            this.Run();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.Now");
        }
    };

    /** This panel's archive is gone - nothing it hears afterwards may paint. */
    public Stop = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Stopped = true;
            this.Remote.Stop();
            if (this.Timer !== 0) { window.clearTimeout(this.Timer); this.Timer = 0; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.Stop");
        }
    };

    private OnDue = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Timer = 0;
            this.Run();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.OnDue");
        }
    };

    /** Paint what the state allows: nothing, the hits, "Searching...", or why not - loading if due. */
    private Run = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            if (AppSearch.Ready(this.Query) === false) { this.Render(""); return; }
            if (this.State.Phase === "ready" && this.State.Data !== null) {
                if (this.Source.CanQueryMessages() === true) {
                    // The ring is decoration beside the words "Searching messages…" (an empty `role="status"` named nothing).
                    this.RenderUnder(AppSearch.Head(AppSearch.Searching) + '<span class="wac-search-busy" aria-hidden="true"></span>');
                    if (this.Acts !== null && this.ActHost !== null) { (this.Acts as AppSearchActRun).Opts = (this.ActHost as IAppSearchActHost).Opts(); }
                    this.Remote.Search(this.State.Data, this.Query, this.Filter, this.RemoteDone); return;
                }
                this.RenderUnder(this.LocalHtml(this.State.Data as AppSearchData));
                return;
            }
            if (this.State.WantLoad(Date.now()) === true) {
                this.State.Begin();
                this.Render(AppSearch.Head(AppSearch.Searching));
                this.Source.LoadPage(AppSearchIndex.FileName, this.OnLoaded, this.OnFailed);
                return;
            }
            if (this.State.Phase === "loading") { this.Render(AppSearch.Head(AppSearch.Searching)); return; }
            this.Render(AppSearch.Head(this.State.Why));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.Run");
        }
    };

    /** The local hits: grouped with their actions when the page gave a host, else `AppSearch`'s plain list. */
    private LocalHtml = (Data: AppSearchData): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (this.Acts === null || this.ActHost === null) { return AppSearch.ResultsHtml(Data, this.Query, this.Filter); }
            const Run: AppSearchActRun = this.Acts as AppSearchActRun;
            Run.Opts = (this.ActHost as IAppSearchActHost).Opts();
            return AppSearchActView.ResultsHtml(Data, this.Query, this.Filter, Run.Opts, Run.Hits);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.LocalHtml"); return ""; }
    };

    private RemoteDone = (Html: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { if (this.Stopped === false) { this.RenderUnder(Html); } }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.RemoteDone"); }
    };

    public OnLoaded = (Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            const Data: AppSearchData = AppSearchIndex.Parse(Text);
            if (Data.Loaded === true) { this.State.Took(Data); }
            else { this.State.Failed(Data.Problem, Date.now()); }
            this.Run();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.OnLoaded");
        }
    };

    /** ⚠ FATE 1: a file not built yet or a dropped connection is SHOWN, in ink, never reported. */
    public OnFailed = (Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            this.State.Failed(AppSearch.LoadWhy(Why), Date.now());
            this.Run();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.OnFailed");
        }
    };

    private Render = (Html: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const El: HTMLElement | null = document.getElementById(AppSearch.HitsId);
            if (El === null) { return; }
            El.innerHTML = Html;
            El.hidden = Html.length === 0;
            El.onclick = this.OnPage;
            El.onchange = this.OnFilter;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.Render");
        }
    };

    /** The part under the filters that a search rewrites. */
    public static readonly ResultsClass: string = "search-results";
    /** The buttons that turn the page of results (local paging, the service's "more", back to the first page). */
    public static readonly PagerSel: string = "[data-search-page], [data-search-more], [data-search-first]";

    /**
     * ⭐ THE FILTERS ARE DRAWN ONCE AND THEN LEFT ALONE - only the results under them are rewritten (audit 2026-10-12).
     * Every search used to repaint the filters with the results: choosing a sender with the arrow keys, or typing a
     * date, destroyed the control in hand and the focus fell to the page after every change - the rule this file
     * already states for the search box ("repainting the input would take focus and the caret away mid-word").
     * ⚠ The filters are made from `this.Filter` whenever they are missing (a repainted list), so they never disagree
     * with the search that ran.
     * ⭐ A paging button that was pressed is gone with its page: the focus goes to the first result of the new page
     * (`PagerSel`), never to nowhere.
     */
    private RenderUnder = (Html: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const El: HTMLElement | null = document.getElementById(AppSearch.HitsId);
            if (El === null) { return; }
            let Results: HTMLElement | null = El.querySelector("." + AppSearchPanel.ResultsClass);
            if (Results === null || El.querySelector(".search-filters") === null) {
                El.innerHTML = this.Controls() + "<div class=\"" + AppSearchPanel.ResultsClass + "\"></div>";
                Results = El.querySelector("." + AppSearchPanel.ResultsClass);
                El.onclick = this.OnPage;
                El.onchange = this.OnFilter;
            }
            if (Results === null) { return; }
            const Active: Element | null = document.activeElement;
            // ⚠ REMEMBERED ACROSS PAINTS: the service's page arrives after a "Searching…" paint that already took the button.
            if (Active !== null && Results.contains(Active) === true && Active.matches(AppSearchPanel.PagerSel) === true) { this.Paging = true; }
            Results.innerHTML = Html;
            El.hidden = false;
            if (this.ActHost !== null) { (this.ActHost as IAppSearchActHost).Painted(Results); }
            if (this.Paging === true) {
                const First: HTMLElement | null = Results.querySelector(".hit a, .hit button, .hitsmore button");
                if (First !== null) { First.focus(); this.Paging = false; }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.RenderUnder");
        }
    };

    private Controls = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // The markup is `AppSearch.FiltersHtml`'s (pure - the suites and the style guide draw the same thing).
            return AppSearch.FiltersHtml((this.State.Data as AppSearchData).Chats, this.Filter);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.Controls"); return ""; }
    };

    private OnFilter = (Event: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target = Event.target as HTMLInputElement;
            const Name: string | null = Target.getAttribute("data-search-filter");
            if (Name === "Href") { this.Filter.Href = Target.value; }
            else if (Name === "FromMe") { this.Filter.FromMe = Target.value; }
            else if (Name === "After") { this.Filter.After = Target.value; }
            else if (Name === "Before") { this.Filter.Before = Target.value; }
            else { return; }
            this.Paging = false;
            this.Filter.Offset = 0; this.Remote.Reset(); this.Run();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.OnFilter"); }
    };

    private OnPage = (Event: MouseEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Target = Event.target as HTMLElement;
            if (this.Acts !== null && (this.Acts as AppSearchActRun).OnClick(Target as unknown as ISaEl) === true) { return; }
            const Button: HTMLElement | null = Target.closest("button");
            if (Button !== null) {
                const Chat: string | null = Button.getAttribute("data-search-chat");
                const Message: string | null = Button.getAttribute("data-search-message");
                if (Chat !== null && Message !== null) { this.Context.Open(Chat, Message); return; }
                if (Button.hasAttribute("data-search-more") === true) { this.Remote.More(); this.Run(); return; }
                if (Button.hasAttribute("data-search-first") === true) { this.Remote.Reset(); this.Run(); return; }
            }
            const Page: string | null = Target.getAttribute("data-search-page");
            if (Page === null) { return; }
            this.Filter.Offset = Number(Page); this.Run();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppSearchPanel.OnPage"); }
    };
}
